import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, Settings, Shield, CreditCard, Truck,
  RefreshCw, CheckCircle, XCircle, Globe, Lock, FileText,
  AlertTriangle, ShoppingCart, Users, Phone, ChevronDown
} from 'lucide-react';

const StoreConfig = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedStoreView, setSelectedStoreView] = useState(0);

  useEffect(() => {
    if (selectedSite) {
      fetchStoreConfig(0);
      setSelectedStoreView(0);
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [selectedSite]);

  const fetchStoreConfig = async (storeView = 0) => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/store-config.php?siteId=${selectedSite.id}&storeView=${storeView}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to fetch store configuration');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreViewChange = (index) => {
    setSelectedStoreView(index);
    fetchStoreConfig(index);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-50 border-red-200 text-red-800';
      case 'medium': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'medium': return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      default: return <AlertCircle className="h-5 w-5 text-blue-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading store configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="h-8 w-8 text-blue-600" />
              Store Configuration Review
            </h1>
            <p className="text-gray-600 mt-2">Review checkout settings, payment options, and trust indicators</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Store View Selector */}
            {data?.available_stores && data.available_stores.length > 1 && (
              <div className="relative">
                <select
                  value={selectedStoreView}
                  onChange={(e) => handleStoreViewChange(parseInt(e.target.value))}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {data.available_stores.map((store) => (
                    <option key={store.index} value={store.index}>
                      {store.name || store.code}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            )}
            <button
              onClick={() => fetchStoreConfig(selectedStoreView)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {data && (
          <div className="space-y-6">
            {/* Recommendations Alert */}
            {data.recommendations?.length > 0 && (
              <div className="space-y-3">
                {data.recommendations.map((rec, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(rec.severity)}`}>
                    <div className="flex items-start gap-3">
                      {getSeverityIcon(rec.severity)}
                      <div>
                        <p className="font-medium">{rec.title}</p>
                        <p className="text-sm mt-1 opacity-80">{rec.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Store Config */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" />
                Store Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Locale</p>
                  <p className="font-medium text-gray-900">{data.store_config?.locale || 'N/A'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Timezone</p>
                  <p className="font-medium text-gray-900">{data.store_config?.timezone || 'N/A'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Base Currency</p>
                  <p className="font-medium text-gray-900">{data.store_config?.base_currency || 'N/A'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Display Currency</p>
                  <p className="font-medium text-gray-900">{data.store_config?.display_currency || 'N/A'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Weight Unit</p>
                  <p className="font-medium text-gray-900">{data.store_config?.weight_unit || 'N/A'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">HTTPS Enabled</p>
                  <p className="font-medium flex items-center gap-2">
                    {data.store_config?.uses_https ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-green-600">Yes</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="text-red-600">No</span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* URLs */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500 min-w-[100px]">Base URL:</span>
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs flex-1 truncate">
                      {data.store_config?.base_url || 'N/A'}
                    </code>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500 min-w-[100px]">Secure URL:</span>
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs flex-1 truncate">
                      {data.store_config?.secure_base_url || 'N/A'}
                    </code>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Checks */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Security Checks
              </h3>
              <div className="space-y-3">
                {data.security && Object.entries(data.security).map(([key, check]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{check.name}</p>
                      <p className="text-sm text-gray-500">{check.description}</p>
                    </div>
                    {check.status ? (
                      <span className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        <CheckCircle className="h-4 w-4" />
                        Pass
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                        <XCircle className="h-4 w-4" />
                        Fail
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-600" />
                Payment Methods
              </h3>
              {data.payment_methods?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.payment_methods.map((method, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{method.title}</p>
                        <p className="text-xs text-gray-500">{method.code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <p className="text-yellow-800">
                      {data.payment_methods_note || 'Unable to retrieve payment methods. This may require specific API permissions.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Shipping Methods */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-blue-600" />
                Shipping Methods
              </h3>
              {data.shipping_methods?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.shipping_methods.map((method, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <Truck className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{method.carrier_title || method.method_title || 'Shipping Method'}</p>
                        {method.amount !== undefined && (
                          <p className="text-xs text-gray-500">${parseFloat(method.amount).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <p className="text-yellow-800">
                      {data.shipping_methods_note || 'Unable to retrieve shipping methods. This may require specific API permissions.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Trust Indicators (CMS Pages)
              </h3>

              {data.trust_indicators?.found_pages && Object.keys(data.trust_indicators.found_pages).length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Found Trust Pages:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(data.trust_indicators.found_pages).map(([key, page]) => (
                      <div key={key} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-900">{page.title}</p>
                          <p className="text-xs text-green-700">/{page.identifier}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.trust_indicators?.missing_pages?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Recommended Missing Pages:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {data.trust_indicators.missing_pages.map((page) => (
                      <div key={page} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                        <p className="font-medium text-orange-900 capitalize">{page.replace('-', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!data.trust_indicators?.found_pages || Object.keys(data.trust_indicators.found_pages).length === 0) &&
               (!data.trust_indicators?.missing_pages || data.trust_indicators.missing_pages.length === 0) && (
                <p className="text-gray-500">No trust page data available</p>
              )}
            </div>

            {/* Checkout Settings Note */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
                Checkout Settings
              </h3>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Guest Checkout Status</p>
                    <p className="mt-1">
                      Guest checkout settings require direct admin access to verify.
                      Check Magento Admin → Stores → Configuration → Sales → Checkout → Checkout Options.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">About this data</p>
                  <p>Configuration data is retrieved from Magento's store config API. Some settings like payment and shipping methods require specific API permissions or cart context to retrieve. Trust indicators are detected from CMS page identifiers.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreConfig;
